/**
 * PartnerSelectList — E-93 Story 93.1 AC-5.
 *
 * Checkbox table for the filter results. Owns nothing — the parent provides
 * the partner slice, the selection hook, and the pagination controls.
 * Unsubscribed partners are rendered disabled (ADR-109 J-1 hard opt-in).
 */

import type { EligiblePartner, PartnerSegmentDto } from '@/api/bulk-email';
import type { BulkSelectionReturn } from '@/hooks/use-bulk-selection';
import { useTranslation } from '@kgc/i18n';
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kgc/ui';
import type { JSX } from 'react';

/** NFR1 — maximum partners selectable in one bulk send. */
export const MAX_SELECTION = 500;

export interface PartnerSelectListProps {
  partners: EligiblePartner[];
  totalCount: number;
  hasMore: boolean;
  onLoadMore: () => void;
  onMaxReached: () => void;
  selection: BulkSelectionReturn;
  isFetching?: boolean | undefined;
}

export function PartnerSelectList({
  partners,
  totalCount,
  hasMore,
  onLoadMore,
  onMaxReached,
  selection,
  isFetching,
}: PartnerSelectListProps): JSX.Element {
  const { t } = useTranslation();

  // Selectable subset = partners on the current page that are NOT
  // unsubscribed. Select-All respects this mask so leiratkozott rows are
  // never implicitly selected.
  const selectableIds = partners.filter(p => !p.unsubscribed).map(p => p.id);
  const allSelectedOnPage = selection.isAllSelected(selectableIds);

  function handleToggleRow(id: string, unsubscribed: boolean): void {
    if (unsubscribed) return;
    if (!selection.isSelected(id) && selection.selectedCount >= MAX_SELECTION) {
      onMaxReached();
      return;
    }
    selection.toggleSelect(id);
  }

  function handleSelectAll(): void {
    // If the cap would be breached after union, refuse and notify.
    if (!allSelectedOnPage) {
      const candidates = selectableIds.filter(id => !selection.isSelected(id));
      if (selection.selectedCount + candidates.length > MAX_SELECTION) {
        onMaxReached();
        return;
      }
    }
    selection.toggleSelectAll(selectableIds);
  }

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-background py-2">
          <span className="text-sm font-medium" data-testid="filtered-count">
            {t('email.bulkSend.filters.filteredCount', { count: totalCount })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              disabled={selectableIds.length === 0}
            >
              {t('email.bulkSend.filters.selectAll')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={selection.clearSelection}
              disabled={selection.selectedCount === 0}
            >
              {t('email.bulkSend.filters.clearSelection')}
            </Button>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>{t('email.bulkSend.filters.columns.name')}</TableHead>
                <TableHead>{t('email.bulkSend.filters.columns.email')}</TableHead>
                <TableHead>{t('email.bulkSend.filters.columns.segment')}</TableHead>
                <TableHead className="w-24 text-right">
                  {t('email.bulkSend.filters.columns.flags')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    {t('email.bulkSend.filters.emptyState')}
                  </TableCell>
                </TableRow>
              ) : (
                partners.map(partner => (
                  <PartnerRow
                    key={partner.id}
                    partner={partner}
                    selected={selection.isSelected(partner.id)}
                    onToggle={() => handleToggleRow(partner.id, partner.unsubscribed)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {hasMore && (
          <div className="flex justify-center pt-2">
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isFetching}>
              {isFetching
                ? t('email.bulkSend.filters.loading')
                : t('email.bulkSend.filters.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

interface PartnerRowProps {
  partner: EligiblePartner;
  selected: boolean;
  onToggle: () => void;
}

function PartnerRow({ partner, selected, onToggle }: PartnerRowProps): JSX.Element {
  const { t } = useTranslation();
  const checkbox = (
    <Checkbox
      checked={selected}
      onCheckedChange={onToggle}
      disabled={partner.unsubscribed}
      aria-label={partner.name}
    />
  );
  return (
    <TableRow className={partner.unsubscribed ? 'opacity-50' : undefined}>
      <TableCell>
        {partner.unsubscribed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block cursor-not-allowed">{checkbox}</span>
            </TooltipTrigger>
            <TooltipContent>{t('email.bulkSend.filters.unsubscribedTooltip')}</TooltipContent>
          </Tooltip>
        ) : (
          checkbox
        )}
      </TableCell>
      <TableCell className="font-medium">{partner.companyName ?? partner.name}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{partner.email ?? '—'}</TableCell>
      <TableCell>
        <SegmentBadge segment={partner.segment} />
      </TableCell>
      <TableCell className="text-right">
        <PartnerFlags partner={partner} />
      </TableCell>
    </TableRow>
  );
}

interface SegmentBadgeProps {
  segment: PartnerSegmentDto;
}

function SegmentBadge({ segment }: SegmentBadgeProps): JSX.Element {
  const { t } = useTranslation();
  // Tailwind color classes are chosen for dark-mode legibility (no raw
  // text-gray-*; using semantic text-foreground + tinted backgrounds so
  // dark mode still hits 4.5:1 contrast per the CLAUDE.md a11y rule).
  const variant: Record<PartnerSegmentDto, string> = {
    VIP: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
    ACTIVE: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
    AT_RISK: 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-100',
    INACTIVE: 'bg-muted text-muted-foreground',
  };
  const labelKey = segment === 'AT_RISK' ? 'atRisk' : segment.toLowerCase();
  return (
    <Badge className={variant[segment]} variant="outline">
      {t(`email.bulkSend.filters.segment.${labelKey}`)}
    </Badge>
  );
}

interface PartnerFlagsProps {
  partner: EligiblePartner;
}

function PartnerFlags({ partner }: PartnerFlagsProps): JSX.Element {
  const { t } = useTranslation();
  const flags: Array<{ icon: string; title: string }> = [];
  if (partner.marketingConsent && !partner.unsubscribed) {
    flags.push({ icon: '📧', title: t('email.bulkSend.filters.flagMarketingConsent') });
  }
  if (partner.unsubscribed) {
    flags.push({ icon: '⚠️', title: t('email.bulkSend.filters.flagUnsubscribed') });
  }
  if (partner.currentBalance > 0) {
    flags.push({ icon: '💰', title: t('email.bulkSend.filters.flagOutstanding') });
  }
  if (flags.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex gap-1" aria-label={flags.map(f => f.title).join(', ')}>
      {flags.map(f => (
        <span key={f.icon} title={f.title}>
          {f.icon}
        </span>
      ))}
    </span>
  );
}
